import type { QuestionSchema, TemplateFormField } from '@common/types/templateFormFields';
import { INTAKE_PREFILL_TAG_OPTIONS } from '@common/utils/matterFields';
import { matchesConditionalValue } from '@common/utils/questionVisibility';
import { DateTime } from 'luxon';
import {
    BORROWER_INFORMATION,
    CURRENT_ADDRESS,
    INITIAL_DETAILS,
    LENDER_INFORMATION,
    MAILING_INFO,
    PROPERTY_DETAILS,
    PURCHASER_INFORMATION,
    SELLER_INFORMATION,
} from '@/constants';

export type GroupedTemplateFormFields = Record<string, TemplateFormField[]>;

/** Groups fields by the builder's copy of their group title (`questionSchema.formGroupTitle`). */
export const generateGroupFieldsData = (
    templateFormFields: TemplateFormField[],
): GroupedTemplateFormFields => {
    return templateFormFields?.reduce<GroupedTemplateFormFields>((acc, field) => {
        const formGroupTitle = field.questionSchema.formGroupTitle ?? '';
        if (!acc[formGroupTitle]) {
            acc[formGroupTitle] = [];
        }
        acc[formGroupTitle].push(field);
        return acc;
    }, {});
};

/** The contact and address groups that V1 real estate forms show for each transaction type. */
const V1_TRANSACTION_GROUPS: Partial<Record<string, string[]>> = {
    Sale: [SELLER_INFORMATION, MAILING_INFO],
    Purchase: [PURCHASER_INFORMATION, MAILING_INFO],
    Refinance: [BORROWER_INFORMATION, CURRENT_ADDRESS],
    PrivateLender: [LENDER_INFORMATION, CURRENT_ADDRESS],
};

/**
 * Builds the fixed V1 real estate groups for a transaction type, in order. V1 groups are static,
 * so every group is kept even when all of its questions have been deleted.
 */
export const prepareGroupTemplate = (
    templateFormFields: TemplateFormField[],
    transactionType: string,
): GroupedTemplateFormFields => {
    if (!templateFormFields?.length) return {};
    const groupedFieldsData = generateGroupFieldsData(templateFormFields);
    const groupTitles = [
        INITIAL_DETAILS,
        ...(V1_TRANSACTION_GROUPS[transactionType] ?? []),
        PROPERTY_DETAILS,
    ];
    return Object.fromEntries(
        groupTitles.map((title) => [title, [...(groupedFieldsData[title] ?? [])]]),
    );
};

export function findConditionalQuestions(
    dataArray: TemplateFormField[] | undefined,
    templateFormFieldId: string,
): TemplateFormField[] {
    const result: TemplateFormField[] = [];
    const mainObject = dataArray?.find((item) => item.templateFormFieldId === templateFormFieldId);

    if (!mainObject) {
        console.log('No object found with the given templateFormFieldId');
        return result;
    }

    const { questionSchema } = mainObject;
    if (questionSchema.conditions && questionSchema.conditions.length > 0) {
        questionSchema.conditions.forEach((condition) => {
            const nextQuestionName = condition.nextQuestion?.name;

            if (nextQuestionName) {
                const matchingObject = dataArray?.find((item) => item.name === nextQuestionName);

                if (matchingObject) {
                    result.push(matchingObject);
                }
            }
        });
    }
    return result;
}

export function getLastQuestionOrder(
    formFields: Pick<TemplateFormField, 'displayOrder'>[] | undefined,
): number {
    let lastOrder = 0;
    for (const field of formFields ?? []) {
        if (field.displayOrder > lastOrder) {
            lastOrder = field.displayOrder;
        }
    }
    return lastOrder;
}

type OperatorType = 'greaterThan' | 'between' | 'lessThan';

interface Fee {
    operatorType: OperatorType;
    value: number[];
    legalFee: number;
}

interface Payload {
    isConductTransactionInToronto: boolean;
    flatFee?: number | string | null;
    thresholdFee?: Fee[];
    pricingModel: string;
}

export function validateNoThresholdOverlap(payload: Payload): boolean {
    const betweenRanges: [number, number][] = [];
    let lessThanValue: number | null = null;
    let greaterThanValue: number | null = null;

    for (const fee of payload.thresholdFee ?? []) {
        const type = fee.operatorType;
        const values = fee.value;

        switch (type) {
            case 'lessThan':
                if (lessThanValue !== null) return false;
                lessThanValue = values[0];
                break;
            case 'greaterThan':
                if (greaterThanValue !== null) return false;
                greaterThanValue = values[0];
                break;
            case 'between':
                if (values[0] === null || values[1] === null) continue;
                if (betweenRanges.some((range) => values[0] < range[1] || values[1] <= range[1]))
                    return false;
                betweenRanges.push([values[0], values[1]]);
                break;
        }
    }

    betweenRanges.sort((a, b) => a[0] - b[0]);

    for (let i = 0; i < betweenRanges.length - 1; i++) {
        if (betweenRanges[i][1] > betweenRanges[i + 1][0]) {
            return false;
        }
    }

    if (lessThanValue !== null && greaterThanValue !== null && lessThanValue >= greaterThanValue) {
        return false;
    }

    return true;
}

export { splitGroupedContactFieldsIntoStore } from './intakeContactSlots';

interface GroupedFieldsDataKeys {
    formFieldId: string;
    formFieldResponse: string;
}

interface GenerateGroupedFieldsDataOptions {
    /** Saved responses, keyed by the property names in `keys`. */
    mappedData?: Record<string, string | null | undefined>[];
    templateFormFields?: TemplateFormField[];
    setWrapCount?: (count: number) => void;
    keys?: GroupedFieldsDataKeys;
    clientEmail?: string;
}

export function generateGroupedFieldsData({
    mappedData = [],
    templateFormFields = [],
    setWrapCount,
    keys = {
        formFieldId: 'FormFieldId',
        formFieldResponse: 'FormFieldResponse',
    },
    clientEmail = '',
}: GenerateGroupedFieldsDataOptions): GroupedTemplateFormFields {
    const defaultValues: Record<string, string | null | undefined> = {};

    mappedData.forEach((item) => {
        const formFieldId = item[keys.formFieldId];
        if (formFieldId) {
            defaultValues[formFieldId] = item[keys.formFieldResponse];
        }
    });

    const templateFormFieldsData = templateFormFields.map((field) => {
        const item = { ...field };
        const questionSchema = item.questionSchema;

        if (clientEmail) {
            if (questionSchema.name === 'email') {
                questionSchema.value = clientEmail;
            }
        }

        if (Object.hasOwn(defaultValues, item.templateFormFieldId)) {
            questionSchema.value = defaultValues[item.templateFormFieldId];
            const name = questionSchema.name;

            if (
                name === 'sellersListedOnPurchaseAndSaleAgreement' ||
                name === 'purchasersListedOnPurchaseAndSaleAgreement' ||
                name === 'borrowersList'
            ) {
                if (typeof setWrapCount === 'function') {
                    setWrapCount(Number(questionSchema.value) || 0);
                }
            }
        }
        item.questionSchema = questionSchema;
        return item;
    });

    templateFormFieldsData.forEach((field) => {
        const questionSchema = field.questionSchema;
        if (questionSchema.isConditionalQuestion) {
            const sourceQue = templateFormFieldsData.find((source) => {
                const sourceQuestionSchema = source.questionSchema;
                return sourceQuestionSchema.name === questionSchema.conditionedBy;
            });

            if (sourceQue) {
                const sourceQueSchema = sourceQue.questionSchema;
                field.isVisibleToUser =
                    matchesConditionalValue(
                        sourceQueSchema.value,
                        questionSchema.conditionValue,
                        sourceQueSchema.type,
                    ) && sourceQue.isVisibleToUser;
            }
        }
    });

    return generateGroupFieldsData(templateFormFieldsData);
}

interface QuoteFormEntry {
    Name?: string;
    Value?: string | number | boolean | null;
}

function formatQuoteFormValue(value: QuoteFormEntry['Value']): string {
    return value != null ? String(value).trim() : '';
}

export function buildDefaultValuesFromQuote(
    quoteFormData: (QuoteFormEntry | QuoteFormEntry[] | null | undefined)[],
): Record<string, string> {
    const defaultValues: Record<string, string> = {};
    if (!quoteFormData?.length) return defaultValues;

    const contactArrays = quoteFormData.filter(Array.isArray);
    const contactCount = contactArrays.length;

    if (contactCount > 0) {
        const tempArrays: Record<string, string[]> = {};

        contactArrays.forEach((arr, contactIdx) => {
            arr.forEach((item) => {
                const name = item?.Name;
                if (name === undefined) {
                    return;
                }
                const value = formatQuoteFormValue(item.Value);

                if (!tempArrays[name]) {
                    tempArrays[name] = Array.from({ length: contactCount }, () => '');
                }
                tempArrays[name][contactIdx] = value;
            });
        });

        Object.keys(tempArrays).forEach((name) => {
            const arr = tempArrays[name];
            defaultValues[name] = contactCount === 1 ? (arr[0] ?? '') : arr.join('|');
        });

        quoteFormData.forEach((entry) => {
            if (!Array.isArray(entry) && entry && typeof entry === 'object') {
                const name = entry.Name;
                if (name !== undefined && !(name in tempArrays)) {
                    defaultValues[name] = formatQuoteFormValue(entry.Value);
                }
            }
        });

        return defaultValues;
    }

    quoteFormData.forEach((entry) => {
        if (entry && !Array.isArray(entry) && entry.Name !== undefined) {
            defaultValues[entry.Name] = formatQuoteFormValue(entry.Value);
        }
    });

    return defaultValues;
}

interface IntakePrefillFormEntry {
    Name?: string;
    Value?: string | null;
}

interface IntakePrefillApiData {
    formResponse?: string;
    newFormResponse?: string;
}

export function formatClosingDatePrefillValue(value: string): string {
    if (!value) {
        return '';
    }

    const fromIso = DateTime.fromISO(value);
    if (fromIso.isValid) {
        return fromIso.toFormat('MM/dd/yyyy');
    }

    const fromJsDate = DateTime.fromJSDate(new Date(value));
    if (fromJsDate.isValid) {
        return fromJsDate.toFormat('MM/dd/yyyy');
    }

    return value;
}

function formatIsoDatePrefillValue(value: string): string {
    if (!/^\d{4}-\d{2}-\d{2}/.test(value.trim())) {
        return value;
    }

    return formatClosingDatePrefillValue(value);
}

export function parseIntakePrefillResponse(apiData: IntakePrefillApiData | null | undefined) {
    let formResponse: IntakePrefillFormEntry[] = [];
    let userContactDetailsArray: IntakePrefillFormEntry[] = [];

    if (apiData?.formResponse) {
        formResponse = JSON.parse(apiData.formResponse);
    }

    if (apiData?.newFormResponse) {
        const parsedData: IntakePrefillFormEntry[] = JSON.parse(apiData.newFormResponse);
        const userContactDetails = parsedData.find((item) => item.Name === 'userContactDetails');

        if (userContactDetails?.Value) {
            userContactDetailsArray = JSON.parse(userContactDetails.Value);
        }
    }

    const filteredFormResponse = formResponse.filter((item) => item.Name !== 'additionalQuestions');

    return [...userContactDetailsArray, ...filteredFormResponse];
}

export function applyPrefillValue(
    questionSchema: QuestionSchema,
    defaultValues: Record<string, string>,
) {
    if (!Object.hasOwn(defaultValues, questionSchema.name)) {
        return;
    }

    let value = defaultValues[questionSchema.name];
    if (questionSchema.type === 'date' && value) {
        value = formatIsoDatePrefillValue(value);
    }

    questionSchema.value = value;
}

export function buildPrefillByTag(defaultValues: Record<string, string>): Record<string, string> {
    const prefillByTag: Record<string, string> = {};

    for (const tag of INTAKE_PREFILL_TAG_OPTIONS) {
        const value = defaultValues[tag];
        if (!value) {
            continue;
        }

        prefillByTag[tag] = formatIsoDatePrefillValue(value);
    }

    return prefillByTag;
}

function getTagPrefillValue(
    tags: string[] | undefined,
    prefillByTag: Record<string, string>,
): string | undefined {
    for (const tag of tags ?? []) {
        const prefillValue = prefillByTag[tag];
        if (prefillValue) {
            return prefillValue;
        }
    }
    return undefined;
}

export function applyTagPrefillToQuestionSchema(
    questionSchema: QuestionSchema,
    prefillByTag: Record<string, string>,
): void {
    const currentValue = questionSchema.value;
    if (currentValue != null && currentValue !== '') {
        return;
    }

    const prefillValue = getTagPrefillValue(questionSchema.tags, prefillByTag);
    if (prefillValue) {
        questionSchema.value = prefillValue;
    }
}

export function applyTagPrefill(
    formValues: Record<string, string | null>,
    groupedFields: Record<string, TemplateFormField[]>,
    prefillByTag: Record<string, string>,
): Record<string, string | null> {
    const nextValues = { ...formValues };

    for (const fields of Object.values(groupedFields)) {
        for (const field of fields) {
            const fieldId = field.templateFormFieldId;
            const currentValue = nextValues[fieldId];
            if (currentValue != null && currentValue !== '') {
                continue;
            }

            const prefillValue = getTagPrefillValue(field.questionSchema.tags, prefillByTag);
            if (prefillValue) {
                nextValues[fieldId] = prefillValue;
            }
        }
    }

    return nextValues;
}
